/**
 * Typed API client for the Evib public website.
 *
 * All public endpoints live under /web/. When the site is served by the
 * backend at /web/ (single origin), REACT_APP_API_URL is empty and calls
 * are same-origin. Responses follow the contracts in src/types/models.ts;
 * the wire `_id` identifier is normalised to `id` here.
 */
import type {
    AboutContent,
    BranchServicesResponse,
    LegalPage,
    NearestBranchResult,
    ServiceType,
    ShowcaseItem,
    WebBranchSummary,
    WebService,
    WebServiceDetail,
} from '../types/models';

const API_BASE = process.env.REACT_APP_API_URL || '';

interface ApiFetchOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: unknown;
    headers?: Record<string, string>;
}

/**
 * Generic fetch wrapper for the backend API.
 * All public endpoints are prefixed with /web/.
 */
async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
    const url = `${API_BASE}${path}`;
    const res = await fetch(url, {
        method: options.method || 'GET',
        headers: { 'Content-Type': 'application/json', ...options.headers },
        body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Request failed: ${res.status}`);
    }
    return res.json() as Promise<T>;
}

/** Wire payloads identify records as `_id` — normalise to `id`. */
function withId<T>(raw: T & { _id: string }): Omit<T, '_id'> & { id: string } {
    const id = raw._id;
    const copy = { ...raw } as Record<string, unknown>;
    delete copy._id;
    copy.id = id;
    return copy as Omit<T, '_id'> & { id: string };
}

/**
 * Fetch homepage showcase items (reel slides).
 * GET /web/showcase
 */
export async function fetchShowcase(): Promise<ShowcaseItem[]> {
    const items = await apiFetch<Array<ShowcaseItem & { _id: string }>>('/web/showcase');
    return items.map(withId);
}

/**
 * Fetch services listing, optionally filtered by type.
 * GET /web/services
 * GET /web/services?type=service|event|sauna|package
 */
export async function fetchServices(type?: ServiceType): Promise<WebService[]> {
    const query = type ? `?type=${type}` : '';
    const items = await apiFetch<Array<WebService & { _id: string }>>(`/web/services${query}`);
    return items.map(withId);
}

/**
 * Fetch a single service detail by slug.
 * GET /web/services/{slug}
 */
export async function fetchServiceDetail(slug: string): Promise<WebServiceDetail> {
    const item = await apiFetch<WebServiceDetail & { _id: string }>(`/web/services/${slug}`);
    return withId(item);
}

/**
 * Fetch about page content.
 * GET /web/about
 */
export async function fetchAbout(): Promise<AboutContent> {
    return apiFetch<AboutContent>('/web/about');
}

/**
 * Find the branch nearest to the visitor's coordinates.
 * POST /web/branches/nearest
 *
 * Pass serviceId to have `available` reflect whether that service is
 * offered at the nearest branch.
 */
export async function fetchNearestBranch(
    latitude: number,
    longitude: number,
    serviceId?: string,
): Promise<NearestBranchResult> {
    return apiFetch<NearestBranchResult>('/web/branches/nearest', {
        method: 'POST',
        body: { latitude, longitude, service_id: serviceId ?? null },
    });
}

/**
 * Fetch a legal page (terms of service or privacy policy).
 * GET /web/legal/{key}
 */
export async function fetchLegalPage(key: 'terms' | 'privacy'): Promise<LegalPage> {
    const raw = await apiFetch<LegalPage & { _id: string }>(`/web/legal/${key}`);
    return withId(raw);
}

/**
 * Fetch the active branch directory for the Services page branch picker.
 * GET /web/branches
 */
export async function fetchWebBranches(): Promise<WebBranchSummary[]> {
    return apiFetch<WebBranchSummary[]>('/web/branches');
}

/**
 * Fetch per-branch services (packages, bundles, singles).
 * Prices follow the admin show-prices toggle. Defaults to the first
 * active branch when branchId is omitted.
 * GET /web/branch-services?branch_id={branchId}
 */
export async function fetchBranchServices(branchId?: string): Promise<BranchServicesResponse> {
    const query = branchId ? `?branch_id=${encodeURIComponent(branchId)}` : '';
    return apiFetch<BranchServicesResponse>(`/web/branch-services${query}`);
}

export default apiFetch;
